import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    RadioProps,
    Typography,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch'
import { useProjectSettings } from 'components/WorkspaceEditor/Workspace2Store'
import {
    ElementTreeView,
    FamilyNumberFormat,
    ProjectSettings,
    ProjectSettingsInput,
    UnitsOfMeasure,
} from 'models/projectSettings'
import { DataPrivacySettings } from '@deltek/specification-client/core/Models/dataPrivacySettings'
import { defaultProjSettings } from 'pages/ProjectsPage/projectUtils'
import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
            fontWeight: 400,
            lineHeight: '160%',
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(0, 0),
            paddingBottom: pxToRem(10),
        },
    },
    dialogCont: {
        '&.MuiDialogContent-root': {
            padding: theme.spacing(0),
        },
        fontSize: pxToRem(18),
    },
    saveButton: {
        '&.MuiButton-root': {
            color: '#1899D4',
            fontSize: pxToRem(18),
            width: pxToRem(39 + 16),
            minWidth: pxToRem(39 + 16),
            lineHeight: pxToRem(21),
            marginBottom: theme.spacing(2),
        },
    },
    cancelButton: {
        '&.MuiButton-root': {
            color: '#1899D4',
            fontSize: pxToRem(18),
            width: pxToRem(56 + 16),
            lineHeight: pxToRem(21),
            marginBottom: theme.spacing(2),
        },
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            width: pxToRem(500),
            height: pxToRem(536),
            paddingLeft: pxToRem(25),
            paddingRight: pxToRem(25),
            paddingTop: pxToRem(22),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    radioGroup: {
        marginBottom: pxToRem(4),
        '& .MuiFormControlLabel-root': {
            maxWidth: 450,
            padding: theme.spacing(0, 0.4),
        },
        '& .MuiRadio-colorSecondary.Mui-checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '& .MuiRadio-colorSecondary': {
            color: '#1899D4',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '&.MuiIconButton-root': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '& .MuiIconButton-colorSecondary': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    },
    radioGroupGridItem: {
        paddingTop: theme.spacing(2.5),
        borderBottom: '1px solid rgba(201, 201, 201, 0.54)',
    },
    settingTitle: {
        fontSize: pxToRem(13),
        color: '#777777',
        textTransform: 'capitalize',
        marginBottom: theme.spacing(1),
    },
    optionLabel: {
        fontSize: pxToRem(13),
        color: '#424242',
    },
    dataCollectionContainer: {
        maxWidth: pxToRem(450),
        maxHeight: pxToRem(72),
        paddingTop: pxToRem(2),
        paddingBottom: theme.spacing(1.5),
    },
    toggleSwitchContainer: {
        width: pxToRem(35),
        height: pxToRem(20),
        marginRight: theme.spacing(1),
    },
    secondRadio: {
        width: pxToRem(140),
    },
    firstRadio: {
        width: pxToRem(160),
    },
}))
const RadioControl = (props: RadioProps) => {
    const checkedColor = props.disabled ? '#D0D0D0' : '#1899D4'
    const uncheckedColor = props.disabled ? '#D0D0D0' : '#989898'
    return (
        <Radio
            {...props}
            disableRipple
            icon={
                <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        cx="8.5"
                        cy="8.5"
                        r="8"
                        fill-opacity="0.05"
                        fill={uncheckedColor}
                        stroke={uncheckedColor}
                    />
                </svg>
            }
            checkedIcon={
                <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        cx="8.5"
                        cy="8.5"
                        r="8"
                        fill={checkedColor}
                        stroke={checkedColor}
                    />
                    <circle cx="8.5" cy="8.5" r="3.5" fill="white" />
                </svg>
            }
        />
    )
}

export interface WorkspaceSettingsDialogProps {
    isDialogDisplayed: boolean
    handleDialogClose: VoidFunction
    saveSettings: (input: ProjectSettingsInput) => void
    firmDataPrivacySettings?: DataPrivacySettings
}

const WorkspaceSettingsDialog = ({
    isDialogDisplayed,
    handleDialogClose,
    saveSettings,
    firmDataPrivacySettings,
}: WorkspaceSettingsDialogProps) => {
    const classes = useStyles()
    const { projectId } = useParams<{ projectId?: string }>()

    // When includeInResearchMap is false the firm has opted OUT of the Research Map,
    // so the project-level data-collection toggle should be disabled.
    const isFirmOptedOut = firmDataPrivacySettings?.includeInResearchMap === false

    const projectSettings = useProjectSettings()

    const [projectSettingsLocal, setProjectSettingsLocal] = React.useState<
        Partial<ProjectSettings>
    >(projectSettings ?? defaultProjSettings)
    const [hasChanges, setHasChanges] = React.useState<boolean>(false)

    const updateProjectSettings = useCallback(
        (
            propertyName:
                | 'familyNumberFormat'
                | 'unitOfMeasure'
                | 'collectData'
                | 'elementTreeView',
            updatedValue:
                | FamilyNumberFormat
                | UnitsOfMeasure
                | boolean
                | ElementTreeView
        ) => {
            switch (propertyName) {
                case 'familyNumberFormat': {
                    setProjectSettingsLocal({
                        ...projectSettingsLocal,
                        familyNumberFormat: updatedValue as FamilyNumberFormat,
                    })
                    break
                }
                case 'unitOfMeasure': {
                    setProjectSettingsLocal({
                        ...projectSettingsLocal,
                        unitOfMeasure: updatedValue as UnitsOfMeasure,
                    })
                    break
                }
                case 'collectData': {
                    setProjectSettingsLocal({
                        ...projectSettingsLocal,
                        collectData: updatedValue as boolean,
                    })
                    break
                }
                case 'elementTreeView': {
                    setProjectSettingsLocal({
                        ...projectSettingsLocal,
                        elementTreeView: updatedValue as ElementTreeView,
                    })
                    break
                }
            }

            setHasChanges(true)
        },
        [projectSettingsLocal]
    )

    const toggleCollectData = useCallback(() => {
        updateProjectSettings('collectData', !projectSettingsLocal.collectData)
    }, [projectSettingsLocal, updateProjectSettings])

    const handleSaveSettings = useCallback(() => {
        if (hasChanges) {
            saveSettings({
                collectData: projectSettingsLocal.collectData as boolean,
                familyNumberFormat: projectSettingsLocal.familyNumberFormat as FamilyNumberFormat,
                unitOfMeasure: projectSettingsLocal.unitOfMeasure as UnitsOfMeasure,
                projectId: projectId as string,
                elementTreeView: projectSettingsLocal.elementTreeView as ElementTreeView,
            })
        }
        handleDialogClose()
    }, [
        hasChanges,
        projectId,
        projectSettingsLocal,
        saveSettings,
        handleDialogClose,
    ])

    return (
        <Dialog
            open={isDialogDisplayed}
            data-testid="projectSettingsDailog"
            className={classes.warningDialogCont}
            aria-labelledby="project-settings"
        >
            <DialogTitle className={classes.dialogTitle}>
                Workspace Settings
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <GridContainer direction="column">
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Element Tree Default View
                        </FormLabel>
                        <RadioGroup
                            defaultValue={ElementTreeView.DIVISIONS}
                            value={projectSettingsLocal?.elementTreeView
                                ?.toString()
                                .toUpperCase()}
                            onChange={(_, value) =>
                                updateProjectSettings(
                                    'elementTreeView',
                                    value as ElementTreeView
                                )
                            }
                            className={classes.radioGroup}
                            data-testid="elementTreeViewRadioGroup"
                            row
                        >
                            {Object.keys(ElementTreeView).map((view, index) => {
                                return (
                                    <FormControlLabel
                                        key={`ElementTreeView-${index}`}
                                        value={view.toUpperCase()}
                                        classes={{
                                            label: classes.optionLabel,
                                        }}
                                        data-testid={`FMRadio-${index}`}
                                        control={<RadioControl />}
                                        label={
                                            ElementTreeView[
                                                view as keyof typeof ElementTreeView
                                            ]
                                        }
                                        className={clsx(
                                            index === 0 && classes.firstRadio,
                                            index === 1 && classes.secondRadio
                                        )}
                                        disabled={true} // Disable for now, as this feature is to be implemented later
                                    />
                                )
                            })}
                        </RadioGroup>
                    </GridItem>
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Units of Measure
                        </FormLabel>
                        <RadioGroup
                            value={projectSettingsLocal?.unitOfMeasure
                                ?.toString()
                                .toUpperCase()}
                            onChange={(_, value) =>
                                updateProjectSettings(
                                    'unitOfMeasure',
                                    value as UnitsOfMeasure
                                )
                            }
                            className={classes.radioGroup}
                            data-testid="unitsOfMeasureRadioGroup"
                            row
                        >
                            {Object.keys(UnitsOfMeasure).map((unit, index) => {
                                return (
                                    <FormControlLabel
                                        key={`UMRadio-${index}`}
                                        value={unit.toUpperCase()}
                                        classes={{
                                            label: classes.optionLabel,
                                        }}
                                        data-testid={`UMRadio-${index}`}
                                        control={<RadioControl />}
                                        label={
                                            UnitsOfMeasure[
                                                unit as keyof typeof UnitsOfMeasure
                                            ]
                                        }
                                        className={clsx(
                                            index === 0 && classes.firstRadio,
                                            index === 1 && classes.secondRadio
                                        )}
                                    />
                                )
                            })}
                        </RadioGroup>
                    </GridItem>
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Section Number Format
                        </FormLabel>
                        <RadioGroup
                            value={projectSettingsLocal?.familyNumberFormat
                                ?.toString()
                                .toUpperCase()}
                            onChange={(_, value) =>
                                updateProjectSettings(
                                    'familyNumberFormat',
                                    value as FamilyNumberFormat
                                )
                            }
                            className={classes.radioGroup}
                            data-testid="familyNumFormatRadioGroup"
                            row
                        >
                            {Object.keys(FamilyNumberFormat).map(
                                (format, index) => {
                                    return (
                                        <FormControlLabel
                                            key={`FamNumberFormat-${index}`}
                                            value={format.toUpperCase()}
                                            classes={{
                                                label: classes.optionLabel,
                                            }}
                                            data-testid={`FMRadio-${index}`}
                                            control={<RadioControl />}
                                            label={
                                                FamilyNumberFormat[
                                                    format as keyof typeof FamilyNumberFormat
                                                ]
                                            }
                                            className={clsx(
                                                index === 0 &&
                                                    classes.firstRadio,
                                                index === 1 &&
                                                    classes.secondRadio
                                            )}
                                        />
                                    )
                                }
                            )}
                        </RadioGroup>
                    </GridItem>
                    <GridItem className={classes.radioGroupGridItem}>
                        <FormLabel className={classes.settingTitle}>
                            Data Collection
                        </FormLabel>
                        <GridContainer
                            direction="column"
                            data-testid="dataCollection"
                        >
                            <GridItem>
                                <GridContainer
                                    className={classes.dataCollectionContainer}
                                >
                                    <GridItem
                                        className={
                                            classes.toggleSwitchContainer
                                        }
                                    >
                                        <ToggleSwitch
                                            checked={
                                                !projectSettingsLocal?.collectData as boolean
                                            }
                                            onChange={toggleCollectData}
                                            isWorkspaceToggle
                                            noMargin
                                            sliderTrackColor={
                                                projectSettingsLocal?.collectData
                                                    ? '#D3D3D3'
                                                    : '#1899D4'
                                            }
                                            sliderThumbColor="#FFFFFF"
                                            sliderThumbBorder={
                                                projectSettingsLocal?.collectData
                                                    ? '1px solid #989898'
                                                    : '1px solid #1899D4'
                                            }
                                            disabled={isFirmOptedOut}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Typography
                                            component="span"
                                            className={classes.optionLabel}
                                        >
                                            {isFirmOptedOut
                                                ? 'Data collection is opted out at the account level.'
                                                : 'Project-level data collection opt-out for this project.'}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="cancelButton"
                    className={classes.cancelButton}
                    onClick={handleDialogClose}
                >
                    Cancel
                </Button>
                <Button
                    data-testid="saveButton"
                    className={classes.saveButton}
                    onClick={handleSaveSettings}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default WorkspaceSettingsDialog
